import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, HostListener, Inject, Renderer2 } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';
import { SEOService } from '../../../services/seo.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

interface TimelineCard {
  id: number;
  year: string;
  title: string;
  description: string;
  image: string;
}
interface PillarSlide {
  title: string;
  description: string;
  image: string;
}
interface GalleryItem {
  image: string;
}
interface Visionary {
  name: string;
  role: string;
  image: string;
  category: string;
}
@Component({
  selector: 'app-about-kalpita-page',
  imports: [CommonModule, RouterModule, FaqComponent],
  standalone: true,
  templateUrl: './about-kalpita-page.component.html',
  styleUrl: './about-kalpita-page.component.scss'
})
export class AboutKalpitaPageComponent implements OnInit, OnDestroy, AfterViewInit {
  private jsonLdScript: any;
  constructor(
    private modalService: NgbModal, private seoService: SEOService, private router: Router, private activatedRoute: ActivatedRoute, @Inject(DOCUMENT) private _document: Document, private renderer: Renderer2
  ) { }
  nav(url: any) {
    this.router.navigateByUrl(url)
  }
  // --- ViewChildren for Animations ---
  @ViewChild('topSection') topSection!: ElementRef;
  @ViewChild('bottomSection') bottomSection!: ElementRef;
  @ViewChild('cultureSection') cultureSection!: ElementRef;
  @ViewChild('partnerSection') partnerSection!: ElementRef;
  @ViewChild('tiltCard') tiltCard!: ElementRef;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  // --- Visibility Flags ---
  isTopVisible = false;
  isBottomVisible = false;
  isCultureVisible = false;
  isPartnerVisible = false;
  private observer: IntersectionObserver | null = null;
  private galleryInterval: any;
  private readonly GALLERY_DURATION = 4000;

  aboutKalpitaFaqs: FaqItem[] = [
    {
      question: 'What makes Kalpita different from traditional software firms?',
      answer: `Kalpita Technologies blends AI-powered innovation, human-centered design, and secure, 
      compliant engineering to deliver impactful software solutions and digital transformation projects 
      faster. Our focus on People-First Nearshore Collaboration and continuous learning ensures both 
      efficiency and meaningful outcomes.`
    },
    {
      question: 'Which industries do you specialize in?',
      answer: `We serve a wide range of sectors including Insurtech, Fintech, Healthcare, Banking, 
      Education, E-commerce, and High-Tech industries, providing tailored solutions that drive 
      measurable business results.`
    },
    {
      question: 'How does Kalpita ensure trustworthy project delivery?',
      answer: `We maintain full transparency, clearly defined SLAs, pilot implementations, and 
      strong ownership at every stage. Our ISO 27001 and SOC II certifications further ensure that 
      projects meet the highest standards of security, compliance, and quality.`
    },
    {
      question: 'Can Kalpita support ongoing AI integration after project delivery?',
      answer: `Yes. Our teams provide continuous support, AI optimization, and enhancement, 
      including AI-focused solutions such as chatbots and recruitment tools, to ensure long-term 
      success and scalability for clients worldwide.`
    },
    {
      question: 'What is Kalpita’s approach to global collaboration?',
      answer: `Leveraging our People-First Nearshore Advantage, we align teams across India, the US, 
      and the UK, enabling seamless, cost-effective collaboration while delivering high-quality 
      IT solutions, AI projects, and software services.`
    }
  ];

  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  cards: TimelineCard[] = [
    {
      id: 1,
      year: '2020',
      title: '2018–2020: The Foundation and Early Growth',
      description: `Kalpita Technologies was inaugurated at Manyata Embassy Business Park, Bangalore, with a small but resourceful team driven to make an impact in the IT industry. During this period, we strengthened our SFDC division, onboarding PD1-certified engineers and achieving SF certification for our SAP team. 
                    To enhance our global reach, we launched a virtual office in California, USA, alongside a new corporate office in Bangalore, boosting operational capacity and client engagement.`,
      image: '../../../../assets/about-company/about-kalpita/TheFoundationAndEarlyGrowth.png'
    },
    {
      id: 2,
      year: '2023',
      title: '2021–2023: Credibility, Security, and Milestones',
      description: `Kalpita Technologies achieved ISO 27001 and SOC II certifications, demonstrating our commitment to data security, compliance, and operational excellence. In 2022, we were recognized as a Microsoft Gold Partner, reflecting our expertise in enterprise solutions and cloud computing. 
                    During this period, our employee base grew by 38%, and we were certified as a Great Place to Work, emphasizing a collaborative, people-first culture. By 2023, Kalpita celebrated five years of excellence, expanding its software solutions across FinTech, InsurTech, E-commerce, Healthcare, and High-Tech industries.`,
      image: '../../../../assets/about-company/about-kalpita/2021to2023CredibilitySecurityandMilestones.png'
    },
    {
      id: 3,
      year: '2025',
      title: '2024–Present: Scaling Excellence and Impact',
      description: `In 2024, we onboarded the 7th batch of trainees, now employed as junior software engineers, expanded our Bangalore office, and featured in 40+ press channels, showcasing our growing industry presence. 
                    We also embarked on our AI software journey, developing AI-powered solutions including chatbots and recruitment tools to enhance business processes. In 2025, we expanded our global presence into Latin America and Tier 2 cities in India, further strengthening our reach and impact. 
                    During this period, we maintained Great Place to Work certification for four consecutive years and SOC II certification for five consecutive years, reinforcing our commitment to innovation, security, and excellence in software development.`,
      image: '../../../../assets/about-company/about-kalpita/2024PresentScalingExcellenceandImpact.png'
    }
    // {
    //   id: 4,
    //   year: '2026',
    //   title: 'Future Vision',
    //   description: 'Leading the way in digital transformation and AI-powered solutions.',
    //   image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop'
    // },
    // {
    //   id: 5,
    //   year: '2027',
    //   title: 'Global Reach',
    //   description: 'Expanding our presence globally with cutting-edge technology solutions.',
    //   image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop'
    // }
  ];

  selectedIndex = 1; // Start with middle card selected

  get visibleCards(): Array<{ card: TimelineCard, position: 'top' | 'center' | 'bottom' }> {
    const result = [];

    // Top card
    if (this.selectedIndex > 0) {
      result.push({
        card: this.cards[this.selectedIndex - 1],
        position: 'top' as const
      });
    }

    // Center card (selected)
    result.push({
      card: this.cards[this.selectedIndex],
      position: 'center' as const
    });

    // Bottom card
    if (this.selectedIndex < this.cards.length - 1) {
      result.push({
        card: this.cards[this.selectedIndex + 1],
        position: 'bottom' as const
      });
    }

    return result;
  }

  get showCarouselDots(): boolean {
    return this.cards.length > 3;
  }

  selectCard(position: 'top' | 'center' | 'bottom'): void {
    if (position === 'top' && this.selectedIndex > 0) {
      this.selectedIndex--;
    } else if (position === 'bottom' && this.selectedIndex < this.cards.length - 1) {
      this.selectedIndex++;
    }
  }

  goToCard(index: number): void {
    if (index >= 0 && index < this.cards.length) {
      this.selectedIndex = index;
    }
  }

  get canScrollUp(): boolean {
    return this.selectedIndex > 0;
  }

  get canScrollDown(): boolean {
    return this.selectedIndex < this.cards.length - 1;
  }

  developmentProcess = [
    {
      icon: '../../../../assets/about-company/about-kalpita/AIEmbeddedSoftwareDevelopment.png',
      title: 'AI-Embedded Software Development',
      description: 'Integrating NLP, RAG, and explainable AI into business processes.'
    },
    {
      icon: '../../../../assets/about-company/about-kalpita/CustomProductEngineering.png',
      title: 'Custom Product Engineering',
      description: 'End-to-end digital products that combine agility and reliability.'
    },
    {
      icon: '../../../../assets/about-company/about-kalpita/IndustrySolutions.png',
      title: 'Industry Solutions',
      description: 'Expertise across Insurtech, Fintech, Healthcare, Banking, E-commerce, and Education.'
    },
    {
      icon: '../../../../assets/about-company/about-kalpita/TechModernization.png',
      title: 'Tech Modernization',
      description: 'Turning legacy systems into intelligent, future-ready platforms.'
    },
    {
      icon: '../../../../assets/about-company/about-kalpita/Consulting_Enablement.png',
      title: 'Consulting & Enablement',
      description: 'Helping businesses adopt AI, automation, and near-shore innovation effectively.'
    }
  ];

  activePillarSlide = 1;
  private pillarInterval: any;

  pillarSlides: PillarSlide[] = [
    {
      title: 'AI & Technology Leadership',
      description: 'We lead with innovation, embedding intelligence into every solution.',
      image: '../../../../assets/about-company/about-kalpita/pillars/aiTech.jpeg'
    },
    {
      title: 'People-First Nearshore Advantage',
      description: 'Seamless, cost-effective collaboration aligning teams across India, the US, and the UK.',
      image: '../../../../assets/about-company/about-kalpita/pillars/peopleFirst.jpeg'
    },
    {
      title: 'Investment in People & Process',
      description: 'Training engineers to blend empathy with execution, creating lasting value.',
      image: '../../../../assets/about-company/about-kalpita/pillars/investment.jpeg'
    },
    {
      title: 'Client-Centric Transparency',
      description: 'Candid communication, agile models, and  measurable outcomes.',
      image: '../../../../assets/about-company/about-kalpita/pillars/clientCentric.jpeg'
    },
    {
      title: 'Domain Depth',
      description: 'Trusted expertise in highly regulated industries.',
      image: '../../../../assets/about-company/about-kalpita/pillars/domainDepth.jpeg'
    },
    {
      title: 'Trust & Accountability',
      description: 'Every promise kept, every project delivered with purpose.',
      image: '../../../../assets/about-company/about-kalpita/pillars/trustAccountability.jpeg'
    }
  ];

  startPillarAutoSlide(): void {
    this.stopPillarAutoSlide();
    this.pillarInterval = setInterval(() => {
      this.activePillarSlide = (this.activePillarSlide + 1) % this.pillarSlides.length;
    }, 2000);
  }

  stopPillarAutoSlide(): void {
    if (this.pillarInterval) {
      clearInterval(this.pillarInterval);
    }
  }

  goToPillarSlide(index: number): void {
    this.activePillarSlide = index;
    this.startPillarAutoSlide();
  }

  activeGallerySlide = 0;

  get gallerySlides(): GalleryItem[][] {
    const slides: GalleryItem[][] = [];
    // Chunk logic: Create groups of 4 images
    for (let i = 0; i < this.galleryItems.length; i += 4) {
      slides.push(this.galleryItems.slice(i, i + 4));
    }
    return slides;
  }

  get currentGallerySlide(): GalleryItem[] {
    return this.gallerySlides[this.activeGallerySlide] || [];
  }

  goToGallerySlide(index: number): void {
    if (index >= 0 && index < this.gallerySlides.length) {
      this.activeGallerySlide = index;
    }
  }

  nextGallerySlide(): void {
    if (this.activeGallerySlide < this.gallerySlides.length - 1) {
      this.activeGallerySlide++;
    }
  }

  prevGallerySlide(): void {
    if (this.activeGallerySlide > 0) {
      this.activeGallerySlide--;
    }
  }

  galleryItems: GalleryItem[] = [
    // Slide 1
    { image: '../../../../assets/about-company/about-kalpita/ourCulture/img1.png' }, // Left
    { image: '../../../../assets/about-company/about-kalpita/ourCulture/img3.png' },          // Middle Back
    { image: '../../../../assets/about-company/about-kalpita/ourCulture/img2.png' },       // Middle Front
    { image: '../../../../assets/about-company/about-kalpita/ourCulture/img4.png' },          // Right

    // Slide 2
    { image: '../../../../assets/about-company/about-kalpita/ourCulture/img5.png' },
    { image: '../../../../assets/about-company/about-kalpita/ourCulture/img6.png' },
    { image: '../../../../assets/about-company/about-kalpita/ourCulture/img7.png' },
    { image: '../../../../assets/about-company/about-kalpita/ourCulture/img8.png' }
  ];

  milestones = [
    { number: '30+', label: 'Apps Built' },
    { number: '7+', label: 'Years of Expertise' },
    { number: '130+', label: 'Team Strength' },
    { number: '70', label: 'Happy Clients' },
    { number: '111+', label: 'Projects Delivered' },
    { number: '38%', label: 'Annual Increase in Employees' }
  ];

  // 1. Categories for the Filter Pills
  visionaryCategories: string[] = [
    'All',
    'Executive Leadership',
    'Technology & Innovation',
    'Delivery & Client Success',
    'Operations & Support'
  ];

  selectedCategory: string = 'All';

  // 2. List of Team Members
  allVisionaries: Visionary[] = [
    // Executive Leadership
    { name: 'Kube G', role: 'CEO', category: 'Executive Leadership', image: 'assets/about-company/about-kalpita/leaders/kube.png' },
    { name: 'Kamal Mudigati', role: 'Director & VP of Technology', category: 'Executive Leadership', image: 'assets/about-company/about-kalpita/leaders/kamal.png' },
    // { name: 'Saimullah', role: 'AVP, Delivery', category: 'Executive Leadership', image: 'assets/about-company/about-kalpita/leaders/sam.png' },
    { name: 'Sophia Shrine', role: 'Associate Group Manager – HR', category: 'Executive Leadership', image: 'assets/about-company/about-kalpita/leaders/sophia.png' },
    { name: 'Jency Francis', role: 'Sr. HR Business Partner US Operations', category: 'Executive Leadership', image: 'assets/about-company/about-kalpita/leaders/jency.png' },
    { name: 'Vinay Kumar', role: 'Finance Manager', category: 'Executive Leadership', image: 'assets/about-company/about-kalpita/leaders/vinay.png' },

    // Technology & Innovation
    { name: 'Vinuthan Bhat', role: 'Director of Technology', category: 'Technology & Innovation', image: 'assets/about-company/about-kalpita/leaders/vinuthan.png' },
    { name: 'Nazeeruddin Syed', role: 'QA Practice Head', category: 'Technology & Innovation', image: 'assets/about-company/about-kalpita/leaders/nazeer.png' },
    { name: 'Pavan Kumar', role: 'Architect, .NET', category: 'Technology & Innovation', image: 'assets/about-company/about-kalpita/leaders/pavan.png' },
    { name: 'Radhakrishna Kommidi', role: 'Associate Principal Architect, UI/UX', category: 'Technology & Innovation', image: 'assets/about-company/about-kalpita/leaders/RK.png' },

    // Delivery & Client Success
    { name: 'Chetan Salgar', role: 'Project Manager, Delivery', category: 'Delivery & Client Success', image: 'assets/about-company/about-kalpita/leaders/chetan.png' },
    // { name: 'Puneeth Rao', role: 'Project Manager, Delivery', category: 'Delivery & Client Success', image: 'assets/about-company/about-kalpita/leaders/puneeth.png' },
    // { name: 'Rohit Jee', role: 'Senior Manager-Strategic Accounts', category: 'Delivery & Client Success', image: 'assets/about-company/about-kalpita/leaders/rohit.png' },

    // Operations & Support
    { name: 'Raja Sarathkumar', role: 'IT Executive', category: 'Operations & Support', image: 'assets/about-company/about-kalpita/leaders/raja.png' },
    { name: 'Roshan Rangaswamy', role: 'Digital Marketing Manager', category: 'Operations & Support', image: 'assets/about-company/about-kalpita/leaders/roshan.png' },
    { name: 'Ravi Prakash', role: 'Operations Manager', category: 'Operations & Support', image: 'assets/about-company/about-kalpita/leaders/ravi.png' },
  ];

  // 3. The list currently displayed
  filteredVisionaries: Visionary[] = [];

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.filterVisionaries('All');
    this.startPillarAutoSlide();
    this.startGalleryAutoSlide();
  }

  updateDescription(desc: string, keywords: string, title: string) {
    this.seoService.updateDescription(desc);
    this.seoService.updateMetaKeywords(keywords);
    this.seoService.updateTitle(title);
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.stopPillarAutoSlide();
    this.stopGalleryAutoSlide();

    if (this.observer) {
      this.observer.disconnect();
    }
  }
  injectSchemaMarkup(): void {
    const aboutPageSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          "@id": "https://www.kalpitatechnologies.com/aboutcompany/about-kalpita-technologies#webpage",
          "url": "https://www.kalpitatechnologies.com/aboutcompany/about-kalpita-technologies",
          "name": "About Kalpita Technologies | AI Software Company in Bangalore & USA",
          "description": "About Kalpita Technologies – a global software engineering and AI innovation company empowering businesses with purposeful AI and human-centered software solutions.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "breadcrumb": { "@id": "https://www.kalpitatechnologies.com/aboutcompany/about-kalpita-technologies#breadcrumbs" },
          "inLanguage": "en-US"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/aboutcompany/about-kalpita-technologies#breadcrumbs",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://www.kalpitatechnologies.com/aboutcompany/about-kalpita-technologies" }
          ]
        },
        {
          "@type": "Article",
          "@id": "https://www.kalpitatechnologies.com/aboutcompany/about-kalpita-technologies#about-article",
          "headline": "Empowering Businesses with Purposeful AI and Human-Centered Software Solutions",
          "author": { "@type": "Organization", "name": "Kalpita Technologies", "@id": "https://www.kalpitatechnologies.com/#organization" },
          "datePublished": "2018-01-01",
          "articleBody": "Kalpita Technologies is a global software engineering and AI innovation company, where technology transforms possibilities into tangible outcomes..."
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" },
          "sameAs": [
            "https://in.linkedin.com/company/kalpitatechnologies",
            "https://www.facebook.com/kalpitatechnologies",
            "https://twitter.com/KalpitaTech",
            "https://www.instagram.com/kalpitatechnologies/",
            "https://www.threads.com/@kalpitatechnologies"
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/aboutcompany/about-kalpita-technologies#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What makes Kalpita Technologies different from traditional software firms?",
              "acceptedAnswer": { "@type": "Answer", "text": "Kalpita Technologies blends AI-powered innovation, human-centered design, and secure, compliant engineering..." }
            },
            {
              "@type": "Question",
              "name": "Which industries does Kalpita Technologies specialize in?",
              "acceptedAnswer": { "@type": "Answer", "text": "Kalpita Technologies serves Insurtech, Fintech, Healthcare, Banking, Education, E-commerce, and High-Tech industries..." }
            }
            // Add more FAQ items if needed
          ]
        }
        // Note: You can add the Person/Leader schema nodes here from your JSON file
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(aboutPageSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  startGalleryAutoSlide(): void {
    this.stopGalleryAutoSlide();
    this.galleryInterval = setInterval(() => {
      this.activeGallerySlide =
        (this.activeGallerySlide + 1) % this.gallerySlides.length;
    }, this.GALLERY_DURATION);
  }

  stopGalleryAutoSlide(): void {
    if (this.galleryInterval) {
      clearInterval(this.galleryInterval);
    }
  }

  onGalleryMouseEnter(): void {
    this.stopGalleryAutoSlide();
  }

  onGalleryMouseLeave(): void {
    this.startGalleryAutoSlide();
  }

  setupCultureTilt(): void {
    const cards = document.querySelectorAll(
      '.culture-gallery img'
    );

    cards.forEach((card) => {
      const el = card as HTMLElement;

      const move = (e: MouseEvent) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const maxRotate = 12;

        const rotateX = ((centerY - y) / centerY) * maxRotate;
        const rotateY = ((x - centerX) / centerX) * maxRotate;

        el.style.transform =
          `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.05)`;
      };

      const leave = () => {
        el.style.transform =
          'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';
      };

      el.addEventListener('mousemove', move);
      el.addEventListener('mouseleave', leave);

      // Disable on touch devices
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        el.removeEventListener('mousemove', move);
        el.removeEventListener('mouseleave', leave);
        el.style.transform = 'none';
      }
    });
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    this.setupScrollAnimation();
    this.setup3DTiltEffect();
    this.setupCultureTilt()
  }

  setup3DTiltEffect(): void {
    if (!this.tiltCard) return;

    const card = this.tiltCard.nativeElement;

    const handleMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Calculate center
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Max rotation in degrees
      const maxRotate = 15;

      // Calculate rotation (inverted logic for natural feel)
      const rotateX = ((centerY - y) / centerY) * maxRotate;
      const rotateY = ((x - centerX) / centerX) * maxRotate;

      // Apply transform
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05, 1.05, 1.05)`;
    };

    const handleLeave = () => {
      // Reset position
      card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
    };

    // Attach events
    card.addEventListener('mousemove', handleMove);
    card.addEventListener('mouseleave', handleLeave);
  }

  setupScrollAnimation(): void {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {

          // 1. Top Section (Who We Are)
          if (this.topSection && entry.target === this.topSection.nativeElement) {
            this.isTopVisible = true;
            this.observer?.unobserve(entry.target);
          }

          // 2. Bottom Section (Mission/Values)
          if (this.bottomSection && entry.target === this.bottomSection.nativeElement) {
            this.isBottomVisible = true;
            this.observer?.unobserve(entry.target);
          }

          // 3. Culture Section
          if (this.cultureSection && entry.target === this.cultureSection.nativeElement) {
            this.isCultureVisible = true;
            this.observer?.unobserve(entry.target);
          }

          // 4. Partner Section (Fixes the blank blue screen)
          if (this.partnerSection && entry.target === this.partnerSection.nativeElement) {
            this.isPartnerVisible = true;
            this.observer?.unobserve(entry.target);
          }
        }
      });
    }, {
      threshold: 0.2 // Trigger when 20% of the row is visible
    });

    if (this.topSection) this.observer.observe(this.topSection.nativeElement);
    if (this.bottomSection) this.observer.observe(this.bottomSection.nativeElement);
    if (this.cultureSection) this.observer.observe(this.cultureSection.nativeElement);
    if (this.partnerSection) this.observer.observe(this.partnerSection.nativeElement);
  }

  // 4. Filter Function
  filterVisionaries(category: string): void {
    this.selectedCategory = category;

    if (category === 'All') {
      this.filteredVisionaries = [...this.allVisionaries];
    } else {
      this.filteredVisionaries = this.allVisionaries.filter(
        v => v.category === category
      );
    }

    // 🔥 FORCE animation recalculation
    setTimeout(() => {
      const track = document.querySelector(
        '.visionary-track.animate-flow'
      ) as HTMLElement;

      if (track) {
        const halfWidth = track.scrollWidth / 2;

        // Distance
        track.style.setProperty('--scroll-distance', `-${halfWidth}px`);

        // ⭐ Calculate duration based on width
        const pixelsPerSecond = 80; // adjust speed here
        const duration = halfWidth / pixelsPerSecond;

        track.style.animationDuration = `${duration}s`;
      }
    });
  }

  exploreOurSolutions(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }
}